import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, Switch, Button, CircularProgress, Alert,
  Chip, IconButton, Tooltip, Tab, Tabs, Avatar, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, Select, MenuItem, FormControl,
  InputLabel, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Save as SaveIcon, Refresh as RefreshIcon, Add as AddIcon, Close as CloseIcon,
  Category as CategoryIcon, Power as PowerIcon, People as PeopleIcon,
  PersonAdd as PersonAddIcon, AdminPanelSettings as AdminIcon,
  Person as PersonIcon, Edit as EditIcon, Delete as DeleteIcon,
  CheckCircle as ActiveIcon, Cancel as InactiveIcon, Lock as LockIcon,
  Visibility as ViewAllIcon, VisibilityOff as ViewAssignedIcon,
  Reply as ReplyIcon, Archive as CloseTicketIcon, SwapHoriz as AssignIcon,
} from '@mui/icons-material';
import api, { extractData } from '../../utils/api';

interface HelpdeskSettingsData {
  enabled: boolean;
  settings: {
    welcomeMessage?: string;
    categories?: string[];
  };
}

interface Agent {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  assignedTickets: number;
  permissions: {
    view: 'all' | 'assigned_only';
    can_reply: boolean;
    can_close: boolean;
    can_assign: boolean;
    is_helpdesk_admin: boolean;
  };
}

interface PortalUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  isActive: boolean;
  ticketCount: number;
  createdAt: string;
}

const defaultCategories = ['billing', 'technical', 'account', 'general'];

// â”€â”€â”€ General Settings Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const GeneralSettingsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState('');

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/support/settings');
      const settings = extractData(res) as HelpdeskSettingsData;
      setEnabled(settings.enabled || false);
      setWelcomeMessage(settings.settings?.welcomeMessage || '');
      setCategories(settings.settings?.categories || defaultCategories);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSave = async () => {
    try {
      setSaving(true); setError(''); setSuccess('');
      await api.put('/support/settings', { enabled, welcomeMessage, categories });
      setSuccess('Settings saved!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim().toLowerCase())) {
      setCategories([...categories, newCategory.trim().toLowerCase()]);
      setNewCategory('');
    }
  };

  if (loading) return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 800, width: '100%', mx: 'auto' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={handleSave}
          disabled={saving} sx={{ textTransform: 'none', borderRadius: '8px' }}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Enable/Disable */}
      <Paper elevation={0} sx={{ p: 3, mb: 2.5, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#6366f115', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1' }}>
            <PowerIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>Helpdesk Portal</Typography>
            <Typography sx={{ color: '#6b7280', fontSize: '0.85rem' }}>
              Enable the public helpdesk portal on helpdesk.yourdomain.com
            </Typography>
          </Box>
          <Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} color="primary" />
        </Box>
        <Box sx={{ mt: 2 }}>
          <TextField label="Welcome Message" fullWidth size="small" multiline rows={2}
            value={welcomeMessage} onChange={e => setWelcomeMessage(e.target.value)}
            placeholder="How can we help you?"
            InputProps={{ sx: { borderRadius: '8px' } }}
          />
          <Typography sx={{ color: '#6b7280', fontSize: '0.8rem', mt: 1 }}>
            Branding (logo, colors, company name) is inherited from the admin panel <strong>White Label</strong> settings.
          </Typography>
        </Box>
      </Paper>

      {/* Categories */}
      <Paper elevation={0} sx={{ p: 3, mb: 2.5, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#10b98115', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
            <CategoryIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>Ticket Categories</Typography>
            <Typography sx={{ color: '#6b7280', fontSize: '0.82rem' }}>Categories available when creating tickets</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {categories.map(cat => (
            <Chip key={cat} label={cat} onDelete={() => setCategories(categories.filter(c => c !== cat))}
              deleteIcon={<CloseIcon sx={{ fontSize: '16px !important' }} />}
              sx={{ borderRadius: '8px', fontSize: '0.85rem', fontWeight: 500, textTransform: 'capitalize' }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" placeholder="Add category..." value={newCategory}
            onChange={e => setNewCategory(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCategory()}
            InputProps={{ sx: { borderRadius: '8px' } }} sx={{ flex: 1, maxWidth: 300 }}
          />
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addCategory}
            disabled={!newCategory.trim()} sx={{ textTransform: 'none', borderRadius: '8px' }}>
            Add
          </Button>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.92rem', color: '#0369a1', mb: 1 }}>
          How the Helpdesk Portal Works
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#0c4a6e', fontSize: '0.85rem', lineHeight: 1.8 }}>
          <li>When enabled, a public helpdesk portal is available at <strong>helpdesk.yourdomain.com</strong></li>
          <li>Customers can submit tickets anonymously <strong>or</strong> log in with portal user credentials</li>
          <li>Portal users can track all their ticket history when logged in</li>
          <li>Customers receive email notifications for ticket updates</li>
          <li>Tickets from the portal are marked as "External" in your dashboard</li>
        </Box>
      </Paper>
    </Box>
  );
};

// â”€â”€â”€ Team / Agents Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const TeamsTab: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState<string | null>(null);

  // Determine the logged-in user's role so we know what they can edit
  const myRole = (() => {
    try {
      const raw = localStorage.getItem('accessToken');
      if (!raw) return 'user';
      const payload = JSON.parse(atob(raw.split('.')[1]));
      return payload.role || 'user';
    } catch { return 'user'; }
  })();
  const iAmOrgAdmin = myRole === 'admin' || myRole === 'superadmin';

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/support/agents');
      setAgents(extractData(res) as Agent[] || []);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const updatePermission = async (agentId: string, newPermissions: Partial<Agent['permissions']>) => {
    const agent = agents.find(a => a.id === agentId);
    if (!agent) return;
    const merged = { ...agent.permissions, ...newPermissions };
    setSaving(agentId);
    try {
      await api.put(`/support/agents/${agentId}/permissions`, merged);
      setAgents(prev => prev.map(a => a.id === agentId ? { ...a, permissions: merged } : a));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update permissions');
    } finally {
      setSaving(null);
    }
  };

  const getInitials = (a: Agent) => {
    if (a.firstName) return (a.firstName[0] + (a.lastName?.[0] || '')).toUpperCase();
    return a.email[0].toUpperCase();
  };

  if (loading) return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 1000, width: '100%', mx: 'auto' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={600} color="#374151">
            {agents.length} team member{agents.length !== 1 ? 's' : ''}
          </Typography>
          {!iAmOrgAdmin && (
            <Typography variant="caption" color="#6b7280">
              You have Helpdesk Admin access. Only organization owners can grant/revoke Helpdesk Admin rights.
            </Typography>
          )}
        </Box>
        <Tooltip title="Refresh"><IconButton size="small" onClick={fetchAgents}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {/* Column headers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 100px 1fr 1fr 1fr 1fr', gap: 1, px: 2.5, py: 1.5, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
          <Typography variant="caption" fontWeight={600} color="#6b7280">MEMBER</Typography>
          <Typography variant="caption" fontWeight={600} color="#9333ea">HD ADMIN</Typography>
          <Typography variant="caption" fontWeight={600} color="#6b7280">TICKET VIEW</Typography>
          <Typography variant="caption" fontWeight={600} color="#6b7280">CAN REPLY</Typography>
          <Typography variant="caption" fontWeight={600} color="#6b7280">CAN CLOSE</Typography>
          <Typography variant="caption" fontWeight={600} color="#6b7280">CAN ASSIGN</Typography>
        </Box>

        {agents.map((agent, idx) => {
          const isOrgAdmin = agent.role === 'admin' || agent.role === 'superadmin';
          const isSaving = saving === agent.id;
          const isHelpdeskAdmin = agent.permissions.is_helpdesk_admin;
          // Helpdesk Admin toggle: only org-level admins can change it
          const canToggleHdAdmin = iAmOrgAdmin && !isOrgAdmin;

          return (
            <Box key={agent.id}>
              {idx > 0 && <Divider />}
              <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 100px 1fr 1fr 1fr 1fr', gap: 1, px: 2.5, py: 2, alignItems: 'center' }}>

                {/* Member info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{
                    width: 36, height: 36, fontSize: '0.8rem', fontWeight: 700,
                    bgcolor: isOrgAdmin ? '#6366f1' : isHelpdeskAdmin ? '#9333ea' : '#9ca3af',
                  }}>
                    {getInitials(agent)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="#1f2937">
                      {agent.firstName ? `${agent.firstName} ${agent.lastName || ''}`.trim() : agent.email}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">{agent.email}</Typography>
                      <Chip
                        icon={isOrgAdmin ? <AdminIcon sx={{ fontSize: '11px !important' }} /> : <PersonIcon sx={{ fontSize: '11px !important' }} />}
                        label={agent.role === 'superadmin' ? 'Super Admin' : agent.role}
                        size="small"
                        sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, textTransform: 'capitalize',
                          bgcolor: isOrgAdmin ? '#ede9fe' : '#f3f4f6', color: isOrgAdmin ? '#6d28d9' : '#6b7280',
                          '& .MuiChip-icon': { color: 'inherit' } }}
                      />
                      {agent.assignedTickets > 0 && (
                        <Chip label={`${agent.assignedTickets} open`} size="small"
                          sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#fef3c7', color: '#92400e' }} />
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Helpdesk Admin toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {isOrgAdmin ? (
                    <Tooltip title="Organization admins always have full helpdesk access">
                      <Chip label="Owner" size="small"
                        sx={{ bgcolor: '#ede9fe', color: '#6d28d9', fontSize: '0.68rem', fontWeight: 700, height: 20 }} />
                    </Tooltip>
                  ) : (
                    <Tooltip title={canToggleHdAdmin
                      ? (isHelpdeskAdmin ? 'Revoke helpdesk admin access' : 'Grant helpdesk admin access')
                      : 'Only org owners can grant this'}>
                      <span>
                        <Switch
                          size="small"
                          checked={isHelpdeskAdmin}
                          disabled={isSaving || !canToggleHdAdmin}
                          onChange={e => updatePermission(agent.id, { is_helpdesk_admin: e.target.checked })}
                          sx={{ '& .MuiSwitch-thumb': { bgcolor: isHelpdeskAdmin ? '#9333ea' : undefined } }}
                        />
                      </span>
                    </Tooltip>
                  )}
                </Box>

                {/* Ticket View */}
                <Box>
                  {isOrgAdmin ? (
                    <Chip label="All tickets" size="small" sx={{ bgcolor: '#dcfce7', color: '#166534', fontSize: '0.72rem' }} />
                  ) : (
                    <FormControl size="small" sx={{ minWidth: 130 }} disabled={isSaving}>
                      <Select
                        value={agent.permissions.view}
                        onChange={e => updatePermission(agent.id, { view: e.target.value as 'all' | 'assigned_only' })}
                        sx={{ fontSize: '0.8rem', borderRadius: '8px' }}
                      >
                        <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>All tickets</MenuItem>
                        <MenuItem value="assigned_only" sx={{ fontSize: '0.8rem' }}>Assigned only</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>

                {/* Can Reply */}
                <Box>
                  {isOrgAdmin ? <CheckBadge active={true} /> : (
                    <Switch size="small" checked={agent.permissions.can_reply} disabled={isSaving}
                      onChange={e => updatePermission(agent.id, { can_reply: e.target.checked })} />
                  )}
                </Box>

                {/* Can Close */}
                <Box>
                  {isOrgAdmin ? <CheckBadge active={true} /> : (
                    <Switch size="small" checked={agent.permissions.can_close} disabled={isSaving}
                      onChange={e => updatePermission(agent.id, { can_close: e.target.checked })} />
                  )}
                </Box>

                {/* Can Assign */}
                <Box>
                  {isOrgAdmin ? <CheckBadge active={true} /> : (
                    <Switch size="small" checked={agent.permissions.can_assign} disabled={isSaving}
                      onChange={e => updatePermission(agent.id, { can_assign: e.target.checked })} />
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}

        {agents.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <PeopleIcon sx={{ fontSize: 40, color: '#d1d5db', mb: 1 }} />
            <Typography color="text.secondary" variant="body2">No team members found</Typography>
          </Box>
        )}
      </Paper>

      <Paper elevation={0} sx={{ mt: 2.5, p: 3, borderRadius: '12px', bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#0369a1', mb: 1 }}>Permission Guide</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
          {[
            ['Helpdesk Admin', 'Grants access to Team & Portal Users tabs — can manage staff permissions and create end-customer portal accounts. Only org owners can grant this.'],
            ['Ticket View', 'All — agent sees every ticket in the org. Assigned only — sees only tickets assigned to them.'],
            ['Can Reply', 'Agent can send replies to customers on their tickets.'],
            ['Can Close', 'Agent can mark tickets as resolved or closed.'],
            ['Can Assign', 'Agent can assign/reassign tickets to other agents.'],
          ].map(([label, desc]) => (
            <Box key={label} sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ width: 6, mt: 0.7, height: 6, borderRadius: '50%', bgcolor: '#0369a1', flexShrink: 0 }} />
              <Typography variant="caption" color="#0c4a6e">
                <strong>{label}:</strong> {desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

const CheckBadge: React.FC<{ active: boolean }> = ({ active }) => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <ActiveIcon sx={{ fontSize: 20, color: active ? '#10b981' : '#d1d5db' }} />
  </Box>
);

// â”€â”€â”€ Portal Users Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const PortalUsersTab: React.FC = () => {
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newCompany, setNewCompany] = useState('');
  const [createError, setCreateError] = useState('');

  // Edit dialog
  const [editUser, setEditUser] = useState<PortalUser | null>(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editCompany, setEditCompany] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/support/portal/users');
      setUsers(extractData(res) as PortalUser[] || []);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load portal users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreate = async () => {
    if (!newEmail || !newPassword) return;
    setCreating(true); setCreateError('');
    try {
      await api.post('/support/portal/users', {
        email: newEmail, password: newPassword,
        firstName: newFirstName || undefined,
        lastName: newLastName || undefined,
        company: newCompany || undefined,
      });
      setCreateOpen(false);
      setNewEmail(''); setNewPassword(''); setNewFirstName(''); setNewLastName(''); setNewCompany('');
      fetchUsers();
      setSuccess('Portal user created!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setCreateError(e?.response?.data?.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (u: PortalUser) => {
    try {
      await api.put(`/support/portal/users/${u.id}`, { isActive: !u.isActive });
      setUsers(prev => prev.map(p => p.id === u.id ? { ...p, isActive: !p.isActive } : p));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update user');
    }
  };

  const openEdit = (u: PortalUser) => {
    setEditUser(u);
    setEditFirstName(u.firstName || '');
    setEditLastName(u.lastName || '');
    setEditCompany(u.company || '');
    setEditPassword('');
    setEditError('');
  };

  const handleEditSave = async () => {
    if (!editUser) return;
    setEditSaving(true); setEditError('');
    try {
      await api.put(`/support/portal/users/${editUser.id}`, {
        firstName: editFirstName || undefined,
        lastName: editLastName || undefined,
        company: editCompany || undefined,
        password: editPassword || undefined,
      });
      fetchUsers();
      setEditUser(null);
      setSuccess('User updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setEditError(e?.response?.data?.message || 'Failed to save');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (u: PortalUser) => {
    if (!window.confirm(`Delete portal user ${u.email}? Their tickets will remain.`)) return;
    try {
      await api.delete(`/support/portal/users/${u.id}`);
      setUsers(prev => prev.filter(p => p.id !== u.id));
      setSuccess('User deleted.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to delete user');
    }
  };

  const getInitials = (u: PortalUser) => {
    if (u.firstName) return (u.firstName[0] + (u.lastName?.[0] || '')).toUpperCase();
    return u.email[0].toUpperCase();
  };

  if (loading) return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 900, width: '100%', mx: 'auto' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 1.5 }}>
        <Typography fontWeight={600} color="#374151" flex={1}>
          {users.length} portal user{users.length !== 1 ? 's' : ''} â€” End-customers with login access
        </Typography>
        <Tooltip title="Refresh"><IconButton size="small" onClick={fetchUsers}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
        <Button variant="contained" size="small" startIcon={<PersonAddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}>
          Add Portal User
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2.5fr 1.5fr 80px 80px 90px', gap: 1, px: 2.5, py: 1.5, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
          <Typography variant="caption" fontWeight={600} color="#6b7280">CUSTOMER</Typography>
          <Typography variant="caption" fontWeight={600} color="#6b7280">COMPANY</Typography>
          <Typography variant="caption" fontWeight={600} color="#6b7280">TICKETS</Typography>
          <Typography variant="caption" fontWeight={600} color="#6b7280">STATUS</Typography>
          <Typography variant="caption" fontWeight={600} color="#6b7280">ACTIONS</Typography>
        </Box>

        {users.map((u, idx) => (
          <Box key={u.id}>
            {idx > 0 && <Divider />}
            <Box sx={{ display: 'grid', gridTemplateColumns: '2.5fr 1.5fr 80px 80px 90px', gap: 1, px: 2.5, py: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: u.isActive ? '#6366f1' : '#d1d5db', fontSize: '0.8rem', fontWeight: 700 }}>
                  {getInitials(u)}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={600} color="#1f2937">
                    {u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="#374151">{u.company || 'â€”'}</Typography>
              <Typography variant="body2" color="#374151" fontWeight={500}>{u.ticketCount}</Typography>
              <Box>
                <Chip
                  label={u.isActive ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.68rem', fontWeight: 600,
                    bgcolor: u.isActive ? '#dcfce7' : '#fee2e2',
                    color: u.isActive ? '#166534' : '#991b1b',
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Edit user">
                  <IconButton size="small" onClick={() => openEdit(u)} sx={{ color: '#6b7280' }}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={u.isActive ? 'Deactivate' : 'Activate'}>
                  <IconButton size="small" onClick={() => handleToggleActive(u)}
                    sx={{ color: u.isActive ? '#ef4444' : '#10b981' }}>
                    {u.isActive ? <InactiveIcon sx={{ fontSize: 16 }} /> : <ActiveIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete user">
                  <IconButton size="small" onClick={() => handleDelete(u)} sx={{ color: '#9ca3af' }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        ))}

        {users.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <PeopleIcon sx={{ fontSize: 40, color: '#d1d5db', mb: 1 }} />
            <Typography color="text.secondary" variant="body2" mb={2}>No portal users yet</Typography>
            <Button variant="outlined" size="small" startIcon={<PersonAddIcon />}
              onClick={() => setCreateOpen(true)} sx={{ textTransform: 'none', borderRadius: '8px' }}>
              Add First Portal User
            </Button>
          </Box>
        )}
      </Paper>

      <Paper elevation={0} sx={{ mt: 2.5, p: 3, borderRadius: '12px', bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#0369a1', mb: 0.75 }}>About Portal Users</Typography>
        <Typography variant="body2" color="#0c4a6e" sx={{ lineHeight: 1.7 }}>
          Portal users are your end-customers who can log in to <strong>helpdesk.yourdomain.com/portal/login</strong> to submit and track their support tickets.
          Create accounts here and share the credentials with your customers. They will only see their own tickets and cannot access any admin features.
        </Typography>
      </Paper>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Portal User</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField label="First Name" size="small" fullWidth value={newFirstName}
              onChange={e => setNewFirstName(e.target.value)} />
            <TextField label="Last Name" size="small" fullWidth value={newLastName}
              onChange={e => setNewLastName(e.target.value)} />
          </Box>
          <TextField label="Email address *" type="email" size="small" fullWidth required
            value={newEmail} onChange={e => setNewEmail(e.target.value)} sx={{ mb: 2 }} />
          <TextField label="Password *" type="password" size="small" fullWidth required
            value={newPassword} onChange={e => setNewPassword(e.target.value)} sx={{ mb: 2 }}
            helperText="Share this with the customer so they can log in" />
          <TextField label="Company (optional)" size="small" fullWidth value={newCompany}
            onChange={e => setNewCompany(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !newEmail || !newPassword}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
            {creating ? <CircularProgress size={18} /> : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onClose={() => !editSaving && setEditUser(null)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Portal User</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField label="First Name" size="small" fullWidth value={editFirstName}
              onChange={e => setEditFirstName(e.target.value)} />
            <TextField label="Last Name" size="small" fullWidth value={editLastName}
              onChange={e => setEditLastName(e.target.value)} />
          </Box>
          <TextField label="Company" size="small" fullWidth value={editCompany}
            onChange={e => setEditCompany(e.target.value)} sx={{ mb: 2 }} />
          <TextField label="New Password (leave blank to keep current)" type="password" size="small" fullWidth
            value={editPassword} onChange={e => setEditPassword(e.target.value)}
            InputProps={{ startAdornment: <LockIcon sx={{ fontSize: 16, color: '#9ca3af', mr: 1 }} /> }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditUser(null)} disabled={editSaving} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
            {editSaving ? <CircularProgress size={18} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// â”€â”€â”€ Main Settings Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const HelpdeskSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 1.5 }}>Helpdesk Settings</Typography>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ minHeight: 36,
          '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', px: 2 } }}>
          <Tab label="General" />
          <Tab label="Team & Permissions" />
          <Tab label="Portal Users" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {activeTab === 0 && <GeneralSettingsTab />}
        {activeTab === 1 && <TeamsTab />}
        {activeTab === 2 && <PortalUsersTab />}
      </Box>
    </Box>
  );
};

export default HelpdeskSettings;
